import React, { useState, useEffect } from 'react';
import { X, Search, Users, Check, AlertTriangle, Save, Crown, UserCheck, Eye } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { ProjectService } from '../../services/projectService';
import { GoogleDirectoryService, GoogleDirectoryUser } from '../../services/googleDirectory';
import { Project, ProjectMember } from '../../types';

interface ProjectTeamModalProps {
  isOpen: boolean;
  onClose: () => void;
  project: Project;
  onTeamUpdated?: () => void;
}

export const ProjectTeamModal: React.FC<ProjectTeamModalProps> = ({
  isOpen,
  onClose,
  project,
  onTeamUpdated
}) => {
  const [users, setUsers] = useState<GoogleDirectoryUser[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<GoogleDirectoryUser[]>([]);
  const [projectMembers, setProjectMembers] = useState<ProjectMember[]>([]);
  const [selectedMembers, setSelectedMembers] = useState<Map<string, ProjectMember['role']>>(new Map());
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  
  const { firebaseUser } = useAuth();
  const projectService = ProjectService.getInstance();
  const directoryService = GoogleDirectoryService.getInstance();

  // Load existing project members
  const loadProjectMembers = async () => {
    try {
      console.log('📋 Loading existing project members...');
      const members = await projectService.getProjectMembers(project.id);
      console.log('✅ Found', members.length, 'existing project members');
      
      setProjectMembers(members);
      
      // Pre-select existing members with their roles
      const memberMap = new Map<string, ProjectMember['role']>();
      members.forEach(member => {
        memberMap.set(member.userId, member.role);
      });
      setSelectedMembers(memberMap);
    } catch (error) {
      console.error('❌ Error loading existing project members:', error);
      // Don't show error to user, just continue without pre-selection
    }
  };

  // Load team members from Google Directory API (via Firebase Functions)
  const loadOrganizationUsers = async () => {
    if (!firebaseUser) {
      setError('Not authenticated');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      console.log('🔍 Loading organization users from Google Directory...');
      
      // Use the Google Directory Service (calls Firebase Functions with service account)
      const orgUsers = await directoryService.getOrganizationUsers(firebaseUser);
      
      // Filter out the project owner (they're automatically included)
      const filteredOrgUsers = orgUsers.filter(user => user.id !== project.ownerId);
      
      console.log('✅ Loaded users:', filteredOrgUsers.length);
      setUsers(filteredOrgUsers);
      setFilteredUsers(filteredOrgUsers);
    } catch (error: any) {
      console.error('❌ Error loading organization users:', error);
      
      if (error.message?.includes('Insufficient permissions')) {
        setError('Admin permissions required. Please contact your administrator.');
      } else {
        setError(`Failed to load team members: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Filter users based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredUsers(users);
    } else {
      const filtered = users.filter(user =>
        user.name.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.primaryEmail.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
    }
  }, [searchTerm, users]);

  // Load data when modal opens
  useEffect(() => {
    if (isOpen) {
      loadProjectMembers().then(() => {
        loadOrganizationUsers();
      });
    }
  }, [isOpen]);

  const handleMemberToggle = (userId: string, role: ProjectMember['role'] = 'viewer') => {
    const newSelected = new Map(selectedMembers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.set(userId, role);
    }
    setSelectedMembers(newSelected);
  };

  const handleRoleChange = (userId: string, newRole: ProjectMember['role']) => {
    const newSelected = new Map(selectedMembers);
    newSelected.set(userId, newRole);
    setSelectedMembers(newSelected);
  };

  const saveProjectTeam = async () => {
    if (!firebaseUser) return;

    try {
      setSaving(true);
      console.log('💾 Saving project team changes...');

      // Get current members for comparison
      const currentMemberIds = new Set(projectMembers.map(m => m.userId));
      const newMemberIds = new Set(selectedMembers.keys());

      // Remove members no longer selected
      for (const currentMember of projectMembers) {
        if (!newMemberIds.has(currentMember.userId)) {
          await projectService.removeProjectMember(project.id, currentMember.userId);
          console.log('🗑️ Removed member:', currentMember.userId);
        }
      }

      // Add or update members
      for (const [userId, role] of selectedMembers.entries()) {
        if (currentMemberIds.has(userId)) {
          // Update existing member role if changed
          const currentMember = projectMembers.find(m => m.userId === userId);
          if (currentMember && currentMember.role !== role) {
            await projectService.updateProjectMemberRole(project.id, userId, role);
            console.log('🔄 Updated member role:', userId, role);
          }
        } else {
          // Add new member
          await projectService.addProjectMember(project.id, userId, role);
          console.log('➕ Added member:', userId, role);
        }
      }

      console.log('✅ Project team saved successfully');
      
      // Notify parent component
      if (onTeamUpdated) {
        onTeamUpdated();
      }
      
      // Close modal
      onClose();
    } catch (error: any) {
      console.error('❌ Error saving project team:', error);
      setError(`Failed to save team changes: ${error.message || 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const getRoleIcon = (role: ProjectMember['role']) => {
    switch (role) {
      case 'admin': return <Crown className="w-4 h-4 text-purple-500" />;
      case 'editor': return <UserCheck className="w-4 h-4 text-blue-500" />;
      case 'viewer': return <Eye className="w-4 h-4 text-gray-500" />;
      default: return <Eye className="w-4 h-4 text-gray-500" />;
    }
  };

  const getRoleColor = (role: ProjectMember['role']) => {
    switch (role) {
      case 'admin': return 'text-purple-600 bg-purple-50 border-purple-200';
      case 'editor': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'viewer': return 'text-gray-600 bg-gray-50 border-gray-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  if (!isOpen) return null;

  const hasChanges = () => {
    const currentSelections = new Map(selectedMembers);
    const existingSelections = new Map(projectMembers.map(m => [m.userId, m.role]));
    
    if (currentSelections.size !== existingSelections.size) return true;
    
    for (const [userId, role] of currentSelections) {
      if (existingSelections.get(userId) !== role) return true;
    }
    
    return false;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Users className="h-6 w-6 text-green-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Manage Project Team</h2>
              <p className="text-sm text-gray-500">
                Add team members to "{project.name}"
                {projectMembers.length > 0 && (
                  <span className="ml-2 text-green-600">
                    ({projectMembers.length} currently added)
                  </span>
                )}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Search */}
        <div className="p-6 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search team members..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading team members from Google Directory...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center max-w-md">
                <div className="bg-red-50 border border-red-200 rounded-md p-6">
                  <div className="flex items-center justify-center mb-3">
                    <AlertTriangle className="h-8 w-8 text-red-500" />
                  </div>
                  <h3 className="text-lg font-semibold text-red-800 mb-2">Connection Error</h3>
                  <p className="text-red-700 text-sm mb-4">{error}</p>
                  <button
                    onClick={loadOrganizationUsers}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="overflow-y-auto max-h-96">
              {filteredUsers.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No team members found</p>
                  {users.length === 0 && (
                    <p className="text-gray-400 text-sm mt-2">
                      Make sure you have Google Workspace admin permissions
                    </p>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-6">
                  {filteredUsers.map((user) => {
                    const isSelected = selectedMembers.has(user.id);
                    const userRole = selectedMembers.get(user.id) || 'viewer';
                    const isExistingMember = projectMembers.some(member => member.userId === user.id);
                    
                    return (
                      <div
                        key={user.id}
                        className={`p-4 border rounded-lg transition-all duration-200 ${
                          isSelected
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div 
                            className="flex items-center space-x-3 flex-1 cursor-pointer"
                            onClick={() => handleMemberToggle(user.id, 'viewer')}
                          >
                            {user.thumbnailPhotoUrl ? (
                              <img
                                src={user.thumbnailPhotoUrl}
                                alt={user.name.fullName}
                                className="h-10 w-10 rounded-full object-cover"
                              />
                            ) : (
                              <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                <span className="text-sm font-medium text-gray-600">
                                  {user.name.givenName?.charAt(0)}{user.name.familyName?.charAt(0)}
                                </span>
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2">
                                <p className="font-medium text-gray-900 truncate">
                                  {user.name.fullName}
                                </p>
                                {isExistingMember && (
                                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                                    Current
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-500 truncate">
                                {user.primaryEmail}
                              </p>
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-2 ml-2">
                            {isSelected && (
                              <select
                                value={userRole}
                                onChange={(e) => handleRoleChange(user.id, e.target.value as ProjectMember['role'])}
                                className={`text-xs px-2 py-1 rounded border ${getRoleColor(userRole)}`}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <option value="viewer">Viewer</option>
                                <option value="editor">Editor</option>
                                <option value="admin">Admin</option>
                              </select>
                            )}
                            
                            {isSelected ? (
                              <div className="flex items-center">
                                {getRoleIcon(userRole)}
                                <Check className="w-5 h-5 text-green-600 ml-1" />
                              </div>
                            ) : (
                              <div className="w-5 h-5" />
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">
                {selectedMembers.size} member{selectedMembers.size !== 1 ? 's' : ''} selected
              </p>
              {hasChanges() && (
                <p className="text-xs text-green-600 mt-1">
                  You have unsaved changes
                </p>
              )}
            </div>
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={saveProjectTeam}
                disabled={saving || !hasChanges()}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Team
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
